import type { ComponentProps } from "react";
import type { GaraazPlaceEnquiryForm } from "@svan-garaaz/reactsharedlib";

type GaraazPlaceEnquiryFormValues = Parameters<
  NonNullable<ComponentProps<typeof GaraazPlaceEnquiryForm>["onSubmit"]>
>[0];

export type TestPrefillConfig = {
  customerId: string;
  partId: string;
  partNumber?: string;
  partName?: string;
  carMaker?: string;
  remarks?: string;
  orderDate?: string;
  /** Pick a saved customer address by Mongo _id; optional if customer has a default address. */
  shipmentAddressId?: string;
  /** Optional overrides — skipped when customer API data is available. */
  shipmentAddress?: {
    _id?: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    pinCode?: string;
    country?: string;
    countryCode?: string;
    isDefault?: boolean;
    location?: {
      latitude: number;
      longitude: number;
    };
  };
  billingDetails?: {
    billingEntity: string;
    name: string;
    gst?: string;
    address: {
      addressLine1: string;
      addressLine2?: string;
      city: string;
      state: string;
      pinCode?: string;
      country?: string;
    };
  };
};

export type CustomerAddressForPrefill = NonNullable<
  TestPrefillConfig["shipmentAddress"]
>;

export type CustomerForPrefill = {
  _id: string;
  name: string;
  mobileNumber?: string;
  gstin?: string;
  addresses?: CustomerAddressForPrefill[];
  billingAddress?: CustomerAddressForPrefill;
};

export type TestPrefillDefaults = {
  customerId: string;
  partId: string;
  partNumber: string;
  partName: string;
  carMaker: string;
  remarks: string;
  orderDate: Date;
  shipmentAddress: TestPrefillConfig["shipmentAddress"] | null;
  billingDetails: TestPrefillConfig["billingDetails"] | null;
};

const EMPTY_DEFAULTS: TestPrefillDefaults = {
  customerId: "",
  partId: "",
  partNumber: "",
  partName: "",
  carMaker: "",
  remarks: "",
  orderDate: new Date(),
  shipmentAddress: null,
  billingDetails: null,
};

export const parseTestPrefillConfigRaw = (
  raw?: string | null,
): TestPrefillConfig | null => {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed) as TestPrefillConfig;
  } catch {
    return null;
  }
};

export const buildTestPrefillDefaults = (
  config: TestPrefillConfig | null,
): TestPrefillDefaults => {
  if (!config) return { ...EMPTY_DEFAULTS, orderDate: new Date() };

  const shipmentAddress = config.shipmentAddress?.addressLine1
    ? config.shipmentAddress
    : null;

  const orderDate = config.orderDate
    ? new Date(config.orderDate)
    : new Date();

  return {
    customerId: config.customerId ?? "",
    partId: config.partId ?? "",
    partNumber: config.partNumber ?? "",
    partName: config.partName ?? "",
    carMaker: config.carMaker ?? "",
    remarks: config.remarks ?? "",
    orderDate: Number.isNaN(orderDate.getTime()) ? new Date() : orderDate,
    shipmentAddress,
    billingDetails: config.billingDetails ?? null,
  };
};

export const formatOrderDateTimeForForm = (date: Date): string => {
  const safeDate = Number.isNaN(date.getTime()) ? new Date() : date;
  return `${safeDate.getFullYear()}-${String(safeDate.getMonth() + 1).padStart(2, "0")}-${String(safeDate.getDate()).padStart(2, "0")}T${String(safeDate.getHours()).padStart(2, "0")}:${String(safeDate.getMinutes()).padStart(2, "0")}`;
};

export const getPreFillValidationError = (
  defaults: TestPrefillDefaults,
): string | null => {
  if (!defaults.customerId || !defaults.partId) {
    return "Pre-fill defaults are missing or invalid. Add orca_test_order_prefill_config to mobile config.";
  }
  if (!defaults.shipmentAddress) {
    return "No delivery address found. Ensure the customer has a saved address or add shipmentAddress to config.";
  }
  return null;
};

export const pickShipmentAddressFromCustomer = (
  customer: CustomerForPrefill,
  options?: {
    addressId?: string;
    fallback?: TestPrefillConfig["shipmentAddress"] | null;
  },
): CustomerAddressForPrefill | null => {
  const addresses = customer.addresses ?? [];
  if (options?.addressId) {
    const matched = addresses.find(
      (address) => String(address._id) === String(options.addressId),
    );
    if (matched) return matched;
  }

  const fromCustomer =
    addresses.find((address) => address.isDefault) ?? addresses[0] ?? null;
  if (fromCustomer) return fromCustomer;

  return options?.fallback?.addressLine1 ? options.fallback : null;
};

export const buildBillingDetailsFromCustomer = (
  customer: CustomerForPrefill,
  shipmentAddress: CustomerAddressForPrefill,
  fallback?: TestPrefillDefaults["billingDetails"],
): TestPrefillDefaults["billingDetails"] => {
  const billingAddress = customer.billingAddress ?? shipmentAddress;

  return {
    billingEntity: "CUSTOMER",
    name: customer.name || fallback?.name || "",
    gst: customer.gstin ?? fallback?.gst ?? "",
    address: {
      addressLine1:
        billingAddress.addressLine1 ||
        fallback?.address.addressLine1 ||
        shipmentAddress.addressLine1,
      addressLine2:
        billingAddress.addressLine2 ?? fallback?.address.addressLine2,
      city: billingAddress.city || fallback?.address.city || shipmentAddress.city,
      state:
        billingAddress.state || fallback?.address.state || shipmentAddress.state,
      pinCode:
        billingAddress.pinCode ??
        fallback?.address.pinCode ??
        shipmentAddress.pinCode ??
        "",
      country:
        billingAddress.country ??
        fallback?.address.country ??
        shipmentAddress.country ??
        "India",
    },
  };
};

export const hydratePrefillDefaultsFromCustomer = (
  config: TestPrefillConfig,
  customer: CustomerForPrefill,
): TestPrefillDefaults => {
  const base = buildTestPrefillDefaults(config);
  const shipmentAddress = pickShipmentAddressFromCustomer(customer, {
    addressId: config.shipmentAddressId ?? config.shipmentAddress?._id,
    fallback: config.shipmentAddress,
  });

  return {
    ...base,
    shipmentAddress,
    billingDetails: shipmentAddress
      ? buildBillingDetailsFromCustomer(
          customer,
          shipmentAddress,
          config.billingDetails ?? null,
        )
      : config.billingDetails ?? null,
  };
};

export type ResolvedPartForPrefill = {
  _id: string;
  partNumber?: string;
  partName?: string;
  brandId?: string;
  mrp?: number;
  brandName?: string;
  hsnCode?: string;
  rootPartNumber?: string;
};

export const mergeResolvedPartWithDefaults = (
  defaults: TestPrefillDefaults,
  apiPart?: ResolvedPartForPrefill | null,
): ResolvedPartForPrefill => ({
  _id: defaults.partId,
  partNumber: apiPart?.partNumber?.trim() || defaults.partNumber.trim(),
  partName: apiPart?.partName?.trim() || defaults.partName.trim(),
  brandId: apiPart?.brandId,
  mrp: apiPart?.mrp,
  brandName: apiPart?.brandName,
  hsnCode: apiPart?.hsnCode,
  rootPartNumber: apiPart?.rootPartNumber,
});

export const hasResolvablePartDisplay = (part: ResolvedPartForPrefill) =>
  Boolean(part.partNumber?.trim() || part.partName?.trim());

export const buildPartRowForForm = (
  part: ResolvedPartForPrefill,
): GaraazPlaceEnquiryFormValues["partsTableData"][number] => ({
  _id: part._id,
  "Part Name": part.partName ?? "",
  "Part Number": part.partNumber ?? "",
  brandId: part.brandId ?? "",
  orderedQuantity: 1,
  MRP: part.mrp ?? 0,
  actualMrp: part.mrp ?? 0,
  discount: 0,
  Brand: part.brandName ?? "",
  "HSN code": part.hsnCode ?? "",
  "Root Part Number": part.rootPartNumber ?? "",
});

export const buildSelectedCustomerForPrefill = (
  defaults: TestPrefillDefaults,
  customer?: CustomerForPrefill | null,
) => {
  if (customer) {
    return {
      _id: customer._id,
      name: customer.name,
      mobileNumber: customer.mobileNumber ?? "",
      addresses: customer.addresses ?? [],
    };
  }

  const shipmentAddress = defaults.shipmentAddress;
  const addresses = shipmentAddress ? [shipmentAddress] : [];

  return {
    _id: defaults.customerId,
    name: defaults.billingDetails?.name ?? "",
    mobileNumber: "",
    addresses,
  };
};

export const buildBillingDetailsForForm = (
  billingDetails: TestPrefillDefaults["billingDetails"],
): GaraazPlaceEnquiryFormValues["billingDetails"] => {
  if (!billingDetails) return null;

  return {
    billingEntity: billingDetails.billingEntity as NonNullable<
      GaraazPlaceEnquiryFormValues["billingDetails"]
    >["billingEntity"],
    gst: billingDetails.gst ?? "",
    name: billingDetails.name,
    address: {
      addressLine1: billingDetails.address.addressLine1,
      addressLine2: billingDetails.address.addressLine2,
      city: billingDetails.address.city,
      state: billingDetails.address.state,
      pinCode: billingDetails.address.pinCode ?? "",
      country: billingDetails.address.country ?? "India",
    },
  };
};

export const buildFormPrefillValuesFromDefaults = (
  defaults: TestPrefillDefaults,
  resolvedPart: ResolvedPartForPrefill,
  options?: { useCurrentDateTime?: boolean; customer?: CustomerForPrefill | null },
): Partial<GaraazPlaceEnquiryFormValues> =>
  buildFormPrefillValues({
    defaults,
    selectedCustomer: buildSelectedCustomerForPrefill(defaults, options?.customer),
    partRow: buildPartRowForForm(resolvedPart),
    orderDateTime: formatOrderDateTimeForForm(
      options?.useCurrentDateTime ? new Date() : defaults.orderDate,
    ),
  });

export const buildFormPrefillValues = ({
  defaults,
  selectedCustomer,
  partRow,
  orderDateTime,
}: {
  defaults: TestPrefillDefaults;
  selectedCustomer: Record<string, unknown>;
  partRow: GaraazPlaceEnquiryFormValues["partsTableData"][number];
  orderDateTime?: string;
}): Partial<GaraazPlaceEnquiryFormValues> => ({
  selectedCustomer,
  customerAddress: defaults.shipmentAddress,
  orderDateTime: orderDateTime ?? formatOrderDateTimeForForm(defaults.orderDate),
  partsTableData: [partRow],
  carMaker: defaults.carMaker,
  remarks: defaults.remarks,
  billingDetails: buildBillingDetailsForForm(defaults.billingDetails),
  media: [],
  licensePlateNumber: "",
  carModel: "",
  vinNumber: "",
});
